import React, { useCallback } from 'react';
import { createUseStyles } from 'react-jss';

import * as Modal from 'app/components/common/modal/Modal';
import Analytics from 'app/lib/Analytics/Analytics';

import { ReactComponent as SpinnerIcon } from 'assets/icons/spinner.svg';

import { useDispatcher, useSelector } from 'app/store/utils/redux/hooks';
import { IPageGift, VirtualGiftType } from 'app/store/nodes/card/interfaces';
import { useMediaResource } from 'app/lib/MediaHooks';

interface IGift {}

const Gift: React.FC<IGift> = (props) => {
  const classes = useStyles();
  const isLoading = useSelector((state) => state.gift.isLoading);

  const page = useSelector((state) => {
    return state.card.pages[state.flipbook.currentPage] as IPageGift<VirtualGiftType>;
  });

  const gift = page?.items?.gift;
  const { containerRef } = useMediaResource(gift?.image);

  const actions = useDispatcher();

  const handleNow = useCallback(() => {
    Analytics.track().useGiftButtonTapped();
    actions.gift.useGift();
  }, [actions]);

  const handleUseLater = useCallback(() => {
    actions.gift.setOpenGift(false);
    actions.gift.setOpenUseLater(true);
  }, [actions]);

  // todo get from server;
  const expirationDate = null;
  return (
    <div className={classes.componentGift}>
      <div className={classes.body}>
        <div className={classes.title}>
          {`Your ${gift?.currency?.symbol}${gift?.amount} Gift Card`}
        </div>
        {Boolean(expirationDate) && (
          <div className={classes.expirationDate}>
            {expirationDate}
          </div>
        )}
        <div className={classes.subtitle}>
          {gift?.platform?.name}
        </div>
        <div className={classes.logo} ref={containerRef} />
        <div className={classes.description}>
          Tap &quot;use now&quot; when you&apos;re
          <br />
          {' ready to use your gift card!'}
        </div>
        <div className={classes.buttons}>
          <button type="button" className="useLaterButton" onClick={handleUseLater}>use later</button>
          {!isLoading && (
            <button type="button" className="useNowButton" onClick={handleNow}>
              use now
            </button>
          )}
          {isLoading && (
            <button type="button" className="useNowButton loading">
              use now
              <SpinnerIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProxyGift: React.FC<IGift> = (props) => {
  const isOpen = useSelector((state) => state.gift.isOpen);
  const page = useSelector((state) => state?.card?.pages[state?.flipbook?.currentPage] as IPageGift);

  const actions = useDispatcher();

  const handleClose = useCallback(() => {
    actions.gift.setOpenGift(false);
  }, [actions]);

  if (page?.type !== 'PageGift') {
    return null;
  }

  return (
    <Modal.Item
      visible={isOpen}
      hAlign="center"
      vAlign="flex-start"
      onOutsideClick={handleClose}
    >
      <Gift {...props} />
    </Modal.Item>
  );
};

const useStyles = createUseStyles({
  componentGift: {
    position: 'relative',
    marginTop: '9.4rem',
    borderRadius: '2.5rem',
    maxWidth: '30rem',
    background: '#fff',
    boxShadow: '0 0 5.2rem rgba(0,0,0,.35)',
  },
  body: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: '3.5rem 3.7rem',
  },
  title: {
    fontFamily: 'Avenir Next Demi,sans-serif',
    fontWeight: '700',
    fontSize: '2rem',
    paddingBottom: '2.4rem',
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: '400',
    fontSize: '1.6rem',
    textAlign: 'center',
    lineHeight: '2.3rem',
  },
  description: {
    fontSize: '1.6rem',
    textAlign: 'center',
    lineHeight: '2.3rem',
    marginTop: '1.5rem',
  },
  buttons: {
    '&': {
      display: 'flex',
      width: '100%',
      justifyContent: 'space-between',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingTop: '2.4rem',
    },
    '& .useNowButton, & .useLaterButton': {
      color: '#fff',
      border: 0,
      fontSize: '1.6rem',
      fontWeight: '700',
      justifyContent: 'center',
      width: '47%',
      outline: 'none',
      cursor: 'pointer',
      borderRadius: '4.8rem',
      display: 'flex',
      alignItems: 'center',
      lineHeight: '4.8rem',
      padding: '0 3rem',
      whiteSpace: 'nowrap',
    },
    '& .useLaterButton': {
      backgroundColor: '#c5c5c5',
    },
    '& .useNowButton': {
      '&': {
        backgroundColor: '#57cbc0',
      },
      '&.loading': {
        color: 'transparent',
      },
      '& svg': {
        position: 'absolute',
        width: '3.2rem',
        height: '3.2rem',
        stroke: '#69717d',
        fill: '#444',
      },
    },
  },
  logo: {
    '&': {
      position: 'relative',
      maxWidth: '23.2rem',
      margin: '1rem auto 0',
      justifyContent: 'center',
      width: '100%',
      overflow: 'hidden',
      borderRadius: '0.9rem',
      aspectRatio: '226 / 143',
    },
    '& img': {
      position: 'absolute',
      display: 'block',
      width: '100%!important',
      height: 'auto!important',
      maxHeight: 'none!important',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
    },
  },
  amount: {
    '&': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      color: '#fa4d5',
    },
    '& span:first-child': {
      fontFamily: 'Avenir Next,sans-serif',
      fontWeight: '700',
      fontSize: '1.29375em',
    },
    '& span:last-child': {
      lineHeight: '1.1',
      fontSize: '2.26406em',
    },
  },
  expirationDate: {
  },
});

export default ProxyGift;
